import { Modal, useModals } from 'almanac-ui/Modal'
import { SettingsMember } from 'types'
import { lowerCase } from 'lodash'
import Styles from './RequestAnotherReviewModal.module.scss'
import {
  InformalFeedbackRequest,
  InformalFeedbackReviewDismissReason,
} from 'api/clients/InformalFeedbackRequestsClient'
import { ContentComposer } from 'components/ShareSheet/ContentComposer/ContentComposer'
import { useCallback, useRef, useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { DueDatePicker } from 'components/DueDatePicker/DueDatePicker'
import { stopPropagation } from 'utils'
import dayjs from 'dayjs'
import { displayEndDatePickerValue } from 'utils/informalFeedbackRequest'
import { UserBadge } from 'components/ShareSheet/RecipientBadge/UserBadge'
import { useCreateReview } from 'hooks/queries/informalFeedbackRequests/Reviews/useCreateReview'
import { useDismissReview } from 'hooks/queries/informalFeedbackRequests/Reviews/useDismissReview'
import { useUpdateInformalFeedbackRequest } from 'hooks/queries/informalFeedbackRequests/useUpdateInformalFeedbackRequest'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { upsertInformalFeedbackRequest } from 'redux/actions/informalFeedbackRequests'
import { articleMetaSet } from 'redux/actions'
import { upsertReview } from 'redux/actions/reviews'
import { useDispatch } from 'react-redux'
import { getLastReview } from 'screens/Todos/components/Row/ReviewKeyAction/ReviewKeyAction'
import { useRefreshInformalFeedbackRequests } from 'hooks/useRefreshInformalFeedbackRequests'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'

interface Props {
  request: InformalFeedbackRequest
}

export const RequestAnotherReviewContent = observer(({ request }: Props) => {
  const contentRef = useRef(null)
  const { drawersStore } = useRootStore()
  const [message, setMessage] = useState('')
  const { closeModal } = useModals()
  const dispatch = useDispatch()
  const [dueDate, setDueDate] = useState<string>(dayjs().add(7, 'day').format('YYYY-MM-DD'))
  const review = getLastReview(request)
  const article = useCurrentArticleMeta()
  const { refreshRequests } = useRefreshInformalFeedbackRequests({
    almanacFileId: request.almanac_file.id,
    requestId: request.id,
  })

  const { mutate: createReview, isLoading: isCreating } = useCreateReview({
    informalFeedbackRequestId: review.informal_feedback_request_id,
  })
  const { mutate: dismissReview, isLoading: isDismissing } = useDismissReview({
    id: review.id,
    almanacFileId: request.almanac_file.id,
    requestId: request.id,
  })
  const { mutate: updateRequest, isLoading: isUpdating } = useUpdateInformalFeedbackRequest(
    review.informal_feedback_request_id,
  )
  const isLoading = isDismissing || isCreating || isUpdating
  const displayText = (value: string | null) => dayjs(value as string).format('ddd, MMM D')

  const handleError = useCallback((error: any) => {
    reportError(error)
    showErrorMessageToast((error as Error).message)
  }, [])

  const handleClose = () => {
    closeModal()
    drawersStore.closeAllDrawers()
  }

  const handleSubmit = () => {
    updateRequest(
      {
        ends_on: dueDate,
      },
      {
        onError: handleError,
      },
    )
    dismissReview(
      { reason: InformalFeedbackReviewDismissReason.RequestedAnotherReview },
      {
        onSuccess: () => {
          createReview(
            {
              required: review.required || false,
              reviewer_id: review.reviewer.id,
              add_member: false,
              message,
              iteration: review.iteration + 1,
            },
            {
              onError: handleError,
              onSuccess: ({ informal_feedback_review }) => {
                refreshRequests()
                dispatch(upsertInformalFeedbackRequest(request))
                dispatch(upsertReview(request))
                article && dispatch(articleMetaSet({ ...article, workflow_status: undefined }))
                handleClose()
                showToastMessage(
                  `You've requested another review from ${informal_feedback_review.reviewer.display_name}`,
                )
              },
            },
          )
        },
        onError: handleError,
      },
    )
  }

  return (
    <>
      <div className={Styles.body}>
        <div className={Styles.recipientContainer}>
          <UserBadge recipient={review.reviewer as unknown as SettingsMember} />
        </div>
        <div className={Styles.dueDateContainer} onClick={stopPropagation}>
          <span className={Styles.dueDate}>Review by:</span>
          <DueDatePicker
            value={dueDate}
            onChange={(value) => value && setDueDate(value)}
            displayText={displayText}
            minValue={dayjs().format('YYYY-MM-DD')}
            shape="squared"
            size="lg"
            variant="ghost"
            className={Styles.dueDatePicker}
          />
          <span className={Styles.dueDate}>{lowerCase(displayEndDatePickerValue(dueDate))}</span>
        </div>
        <div className={Styles.messageWrapper}>
          <ContentComposer
            ref={contentRef}
            content={message}
            placeholder="Type your message here (optional)..."
            onChange={setMessage}
          />
        </div>
      </div>
      <Modal.Footer className={Styles.footer}>
        <Modal.FooterContent slot="left">
          <Button size="lg" variant="tertiary" onPress={handleClose} isDisabled={isLoading}>
            Close
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button size="lg" variant="primary" onPress={handleSubmit} isDisabled={isLoading}>
            Request Another Review
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
})
